import useDocumentTitle from '@/hooks/useDocumentTitle';
import { CorrectionCanvas } from './CorrectionCanvas.tsx';
import { TreatmentCycles } from './UI/TreatmentCycles.tsx';
import { TreatmentConstraints } from './UI/TreatmentConstraints.tsx';
import { useParams } from 'react-router-dom';
import { useJawsSegmentation } from '@hooks/useJawsSegmentation.ts';
import { useJawsScanSummary } from '@hooks/useJawsScan.ts';
import type { JawsSegmentationResponse } from '@/schema/jawsSegmentation';
import useCorrectionStore from './hooks/useCorrectionStore.ts';
import { ControlsUI } from '@correction/UI/ControlsUI.tsx';
import LoadingPage from '@/components/ui/LoadingPage.tsx';
import useThreeStore from '@/modules/three/hooks/useThreeStore.ts';

const CorrectionContent = ({ segmentation, scanId }: { segmentation: JawsSegmentationResponse; scanId: string }) => {
  useDocumentTitle("Braces Designer - Teeth Correction | fullName");
  const { jaws } = useCorrectionStore();
  const { isLoading: isThreeLoading } = useThreeStore();

  return (
    <>
      <div className={'relative w-full h-full'}>
        {isThreeLoading && <LoadingPage loadingText="Loading correction..." />}
        <CorrectionCanvas jawsSegmentation={segmentation} />
      </div>
      {jaws && (
        <>
          <ControlsUI className="absolute left-3 top-3 z-20" scanId={scanId} />
          <TreatmentConstraints className="absolute right-3 top-3 z-20 w-full max-w-[380px]" />
          <div className="pointer-events-none absolute bottom-3 left-3 z-10 max-w-[calc(100vw-1.5rem)]">
            <div className="pointer-events-auto">
              <TreatmentCycles />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default function Correction() {
  const { scanId } = useParams<{ patientId: string; scanId: string }>();
  const scanSummaryQuery = useJawsScanSummary(scanId ?? '', { enabled: Boolean(scanId) });
  const segmentationId = scanSummaryQuery.data?.jawsSegmentationId ?? null;
  const segmentationQuery = useJawsSegmentation(segmentationId ?? '', { enabled: Boolean(segmentationId) });

  if (!scanId) {
    return <div>Error loading correction data</div>;
  }

  if (scanSummaryQuery.isLoading || (segmentationId && segmentationQuery.isLoading)) {
    return <LoadingPage loadingText="Loading correction..." />;
  }

  if (scanSummaryQuery.isError || !scanSummaryQuery.data) {
    return <div>Error loading scan data</div>;
  }

  if (!segmentationId) {
    return <div>No segmentation available for this scan. Complete segmentation before correcting.</div>;
  }

  if (segmentationQuery.isError || !segmentationQuery.data) {
    return <div>Error loading segmentation data</div>;
  }

  return <CorrectionContent segmentation={segmentationQuery.data} scanId={scanId} />;
}
